import React, { createContext, useContext, useEffect, useMemo } from 'react'
import {
  useDispatch as useReduxDispatch,
  useSelector as useReduxSelector,
  TypedUseSelectorHook,
} from 'react-redux'
import type { DefinedComponent } from '@openuidev/react-lang'
import { chatServiceUrl } from '../../utils/constants'
import { LabraChatThunkDispatch } from './actions'
import {
  openLabraChat,
  sendLabraChatPrompt,
  setLabraChatAgentType,
  startNewLabraChat,
} from './actions'
import { ChatUploadedDocument } from './api'
import {
  selectIsLabraChatLoading,
  selectLabraChatMessages,
  LabraChatSliceState,
} from './selectors'
import { LabraChatMessage } from './reducer'

/**
 * Product-supplied `render_ui` components, merged into the base OpenUI library
 * so a `render_ui` tool call can render product-specific generative UI (e.g. a
 * funding health card). Each entry is an OpenUI `DefinedComponent` built with
 * `defineComponent({ name, schema, render })` from `@openuidev/react-lang`, so
 * the backend can reference it by name in the OpenUI-Lang program. Empty by
 * default — the base library already covers the generic vocabulary (Stack,
 * Card, BarChart, TextContent, …).
 */
export type OpenUiComponentRegistry = ReadonlyArray<DefinedComponent<any>>

export interface LabraChatContextValue {
  /** Partner id used for every chat endpoint (`/v2/partners/{id}/...`). */
  partnerId: string
  /** Account id (kept distinct from partnerId for products that differ). */
  accountId: string
  /** User id sent as `user_id` in chat/session/history requests. */
  userId: string
  /** Base chat service URL; defaults to the shared `chatServiceUrl` constant. */
  chatEndpoint: string
  /** Backend agent selector sent as `agent_type` on the chat stream body. */
  agentType: string
  /** Product-supplied generative-UI components (see OpenUiComponentRegistry). */
  openUiComponents: OpenUiComponentRegistry
}

const LabraChatContext = createContext<LabraChatContextValue | null>(null)

export interface LabraChatProviderProps {
  partnerId: string
  accountId: string
  userId: string
  chatEndpoint?: string
  agentType?: string
  openUiComponents?: OpenUiComponentRegistry
  children: React.ReactNode
}

/**
 * Supplies the ids + endpoint + generative-UI registry the chat shell and its
 * injected slots read, so no chat component has to reach for a host-specific
 * hook (e.g. `useAccountId`). Mount this above `<LabraChatTriggerButton>` /
 * `<LabraChatPanel>` inside a Redux `<Provider>` whose store includes the
 * `labraChat` reducer (the host root store already does).
 */
export const LabraChatProvider: React.FC<LabraChatProviderProps> = ({
  partnerId,
  accountId,
  userId,
  chatEndpoint = chatServiceUrl,
  agentType = '',
  openUiComponents,
  children,
}) => {
  const dispatch = useReduxDispatch()

  useEffect(() => {
    dispatch(setLabraChatAgentType(agentType))
  }, [dispatch, agentType])

  const value = useMemo<LabraChatContextValue>(
    () => ({
      partnerId,
      accountId,
      userId,
      chatEndpoint,
      agentType,
      openUiComponents: openUiComponents ?? [],
    }),
    [partnerId, accountId, userId, chatEndpoint, agentType, openUiComponents]
  )
  return (
    <LabraChatContext.Provider value={value}>
      {children}
    </LabraChatContext.Provider>
  )
}

/**
 * Read the chat context. Throws when used outside a `<LabraChatProvider>` so a
 * missing provider fails loudly instead of silently sending empty ids.
 */
export const useLabraChatContext = (): LabraChatContextValue => {
  const ctx = useContext(LabraChatContext)
  if (!ctx) {
    throw new Error(
      'useLabraChatContext must be used within a <LabraChatProvider>'
    )
  }
  return ctx
}

/** react-redux dispatch typed to accept the chat thunks. */
export const useLabraChatDispatch = (): LabraChatThunkDispatch =>
  useReduxDispatch<LabraChatThunkDispatch>()

/** react-redux selector typed against the (optional) labraChat slice. */
export const useLabraChatSelector: TypedUseSelectorHook<LabraChatSliceState> =
  useReduxSelector

/**
 * Drive the chat from an injected slot component (e.g. a funding header): send a
 * prompt as the user, reset to a fresh conversation, open the panel, and read
 * the live message list + streaming flag. `partnerId` is taken from context so
 * callers never thread it through.
 */
export const useLabraChat = () => {
  const { partnerId } = useLabraChatContext()
  const dispatch = useLabraChatDispatch()
  const messages: LabraChatMessage[] = useLabraChatSelector(
    selectLabraChatMessages
  )
  const isStreaming = useLabraChatSelector(selectIsLabraChatLoading)

  const sendPrompt = React.useCallback(
    (
      prompt: string,
      options?: {
        showUserMessage?: boolean
        displayMessage?: string
        documents?: ChatUploadedDocument[]
      }
    ) => dispatch(sendLabraChatPrompt(partnerId, prompt, options)),
    [dispatch, partnerId]
  )

  const startNewChat = React.useCallback(
    () => dispatch(startNewLabraChat()),
    [dispatch]
  )

  const open = React.useCallback(() => dispatch(openLabraChat()), [dispatch])

  return { sendPrompt, startNewChat, open, messages, isStreaming, partnerId }
}
